import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select } from "@normasteaching/norma-ui";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { Card } from "../../UI/Card.tsx";
import { LoadingBlock } from "../../UI/Loading/LoadingRows.tsx";
import {
  CloserStatsReportDTO,
  useGetSchedulerCloserStats,
} from "../../HubSpot/useGetSchedulerCloserStats.ts";
import { CheckClosersAvailabilityScreenPath } from "../CheckClosersAvailabilityScreen.tsx";
import { CapTrendChart, CrTrendChart } from "./CloserStatsCharts.tsx";
import { CloserStatsTable } from "./CloserStatsTable.tsx";
import { StatsKpi } from "./StatsKpi.tsx";
import {
  capSaturation,
  dataStartNote,
  formatRate,
} from "./closerStatsFormat.ts";
import { PeriodSelector } from "./PeriodSelector.tsx";
import { defaultStatsPeriod, rangeError } from "./statsPeriod.ts";

export function CloserStatsScreen() {
  const navigate = useNavigate();
  const { getSchedulerCloserStats, loading } = useGetSchedulerCloserStats();
  const [period, setPeriod] = useState(defaultStatsPeriod);
  const [report, setReport] = useState<CloserStatsReportDTO | null>(null);
  const [selectedCloserId, setSelectedCloserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);

  useEffect(() => {
    const range = period.range;
    const invalid = rangeError(range);
    if (invalid) {
      setError(invalid);
      return;
    }
    const current = ++request.current;
    setError(null);
    getSchedulerCloserStats(range)
      .then((data) => {
        if (current === request.current) setReport(data ?? null);
      })
      .catch((err) => {
        console.error("Error fetching closer stats:", err);
        if (current === request.current) {
          setError("Errore nel recupero delle statistiche. Riprova.");
          setReport(null);
        }
      });
  }, [period, getSchedulerCloserStats]);

  const selectedCloser =
    report?.closers.find((c) => c.closerId === selectedCloserId) ?? null;
  const figures = selectedCloser?.totals ?? report?.totals ?? null;
  const weeks = selectedCloser?.weeks ?? report?.weeks ?? [];
  const saturation = figures ? capSaturation(figures) : null;

  const startNote = report
    ? dataStartNote(period.range.fromDay, report.dataFromDay)
    : null;

  return (
    <Layout maxWClass="max-w-full">
      <div className="space-y-4">
        <Card>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-2">
              <button
                onClick={() => navigate(CheckClosersAvailabilityScreenPath)}
                className="text-sm text-primary hover:underline"
              >
                ← Check Closer Availability
              </button>
              <h1 className="font-bold text-2xl">Statistiche closer</h1>
              <p className="text-sm text-gray-500 max-w-3xl">
                Call effettuate, deal vinti, no show e da rifissare arrivano da
                HubSpot con le stesse regole del job CR. Prenotazioni e cap
                contano solo le call dello scheduler; per i giorni passati il
                cap è quello attuale.
              </p>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <PeriodSelector period={period} onChange={setPeriod} />
              <div className="w-56 space-y-1">
                <p className="text-sm font-medium text-gray-700">Closer</p>
                <Select
                  clearable
                  placeholder="Tutti i closer"
                  value={selectedCloserId ?? undefined}
                  onChange={(id) => setSelectedCloserId(id)}
                  options={(report?.closers ?? []).map((c) => ({
                    id: c.closerId,
                    name: c.name,
                  }))}
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}
          {startNote && (
            <p className="mt-3 text-xs text-gray-500">{startNote}</p>
          )}
        </Card>

        {loading && !report && <LoadingBlock sizeClassName="h-64 w-full" />}

        {figures && (
          <>
            <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(170px,1fr))]">
              <StatsKpi
                label="Call effettuate"
                value={figures.callsDone}
                sub="outcome Call effettuata"
              />
              <StatsKpi
                label="Deal vinti"
                value={figures.wonDeals}
                sub="pipeline FPS Sales"
              />
              <StatsKpi
                highlight
                label="CR del periodo"
                value={figures.callsDone === 0 ? "—" : formatRate(figures.cr)}
                sub={
                  selectedCloser
                    ? `CR attuale ${formatRate(selectedCloser.currentCr)}`
                    : "deal vinti / call effettuate"
                }
              />
              <StatsKpi
                label="No show · Da rifissare"
                value={`${figures.noShow} · ${figures.toReschedule}`}
                sub="esiti nel periodo"
              />
              <StatsKpi
                label="Call prenotate (scheduler)"
                value={figures.booked}
                sub={`Landing ${figures.bookedWeb} · Setter ${figures.bookedSetter}`}
              />
              <StatsKpi
                label="Cancellate · Pending"
                value={`${figures.cancelled} · ${figures.pending}`}
                sub={`${figures.reschedules} riprogrammazioni`}
              />
              <StatsKpi
                label="Saturazione cap"
                value={formatRate(saturation)}
                sub={`${figures.callsOnCapacityDays} call su ${figures.capacityTotal} posti`}
              />
              <StatsKpi
                label="Giorni al cap"
                value={figures.daysAtCap}
                sub={`su ${figures.capacityDays} giorni · ${figures.callsOverCap} call oltre il cap`}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <h2 className="font-semibold mb-1">
                  CR per settimana
                  {selectedCloser ? ` · ${selectedCloser.name}` : ""}
                </h2>
                <CrTrendChart weeks={weeks} />
              </Card>
              <Card>
                <h2 className="font-semibold mb-1">
                  Cap per settimana
                  {selectedCloser ? ` · ${selectedCloser.name}` : ""}
                </h2>
                <CapTrendChart weeks={weeks} />
              </Card>
            </div>
          </>
        )}

        {report && (
          <Card>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold">Per closer</h2>
              {selectedCloser && (
                <button
                  onClick={() => setSelectedCloserId(null)}
                  className="text-sm text-primary hover:underline"
                >
                  Mostra tutti
                </button>
              )}
            </div>
            <CloserStatsTable
              closers={report.closers}
              selectedCloserId={selectedCloserId}
              onSelect={(id) =>
                setSelectedCloserId((current) => (current === id ? null : id))
              }
            />
          </Card>
        )}
      </div>
    </Layout>
  );
}
